// utils/fenologia.js
// Funções utilitárias para cálculo de fases fenológicas
import { fenologiaCultural } from '../constants/fenologiaCultural';
import { culturaParaFenologia } from '../constants/culturas';

/**
 * Converte string 'yyyy-mm-dd' para Date local (meia-noite local).
 * Evita o desvio de UTC que ocorre com new Date('yyyy-mm-dd'),
 * que interpreta a string como UTC midnight → dia anterior no Brasil (UTC-3).
 */
function parseLocalDate(str) {
  if (!str) return null;
  if (str instanceof Date) return new Date(str.getFullYear(), str.getMonth(), str.getDate());
  const [y, m, d] = String(str).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Calcula as fases fenológicas (datas de início/fim) para uma cultura, data de plantio e ajustes do usuário.
 * @param {string} culturaBruta - Nome da cultura (ex: 'Milho', 'Soja', 'milho', 'soja')
 * @param {string|Date} dataPlantio - Data de plantio (yyyy-mm-dd ou Date)
 * @param {object} datasAjustadas - Ajustes do usuário (opcional)
 * @returns {Array} Array de fases com nome, data início e data fim
 */
export function calcularFasesFenologicas(culturaBruta, dataPlantio, datasAjustadas = {}) {
  // Mapear cultura para chave fenologia (ex: "Milho" → "milho")
  const cultura = culturaParaFenologia(culturaBruta);
  
  if (!cultura || !fenologiaCultural[cultura] || !dataPlantio) return [];
  const baseFases = fenologiaCultural[cultura];
  let fasesCalculadas = [];
  let inicioFase = datasAjustadas[0]?.inicioFase
    ? parseLocalDate(datasAjustadas[0].inicioFase)
    : parseLocalDate(dataPlantio);
  for (let idx = 0; idx < baseFases.length; idx++) {
    const faseOrig = baseFases[idx];
    // Se o usuário ajustou o fim, usa; senão, calcula pelo padrão
    let fimFase;
    if (datasAjustadas[idx]?.fimFase) {
      fimFase = parseLocalDate(datasAjustadas[idx].fimFase);
    } else {
      fimFase = new Date(inicioFase.getTime() + (faseOrig.dias - 1) * 24 * 60 * 60 * 1000);
    }
    // Se o usuário ajustou o início, usa; senão, mantém
    if (idx > 0 && datasAjustadas[idx]?.inicioFase) {
      inicioFase = parseLocalDate(datasAjustadas[idx].inicioFase);
    }
    fasesCalculadas.push({
      nome: faseOrig.fase,
      idx,
      inicio: new Date(inicioFase),
      fim: new Date(fimFase)
    });
    inicioFase = new Date(fimFase.getTime() + 24 * 60 * 60 * 1000); // Próxima fase começa no dia seguinte
  }
  return fasesCalculadas;
}

/**
 * Retorna o nome da fase vigente para uma data de referência.
 * @param {Array} fases - Array de fases (de calcularFasesFenologicas)
 * @param {Date|string} dataReferencia - Data a ser verificada
 * @returns {string} Nome da fase vigente ou '-'
 */
export function obterFasePorData(fases, dataReferencia) {
  if (!Array.isArray(fases) || !dataReferencia) return '-';
  // Normalizar para meia-noite local para evitar falha de comparação no último dia de uma fase
  const raw = new Date(dataReferencia);
  const data = new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
  for (let i = 0; i < fases.length; i++) {
    if (data >= fases[i].inicio && data <= fases[i].fim) {
      return fases[i].nome;
    }
  }
  // Se passou de todas, retorna a última
  if (fases.length > 0 && data > fases[fases.length - 1].fim) {
    return fases[fases.length - 1].nome;
  }
  return '-';
}
